import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
  FormArray,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RestaurantService } from '../../../../../services/restaurant.service';
import { isPosManaged } from '../../../../../utils/pos-provider.util';

@Component({
  selector: 'app-add-customization-modal',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './add-customization-modal.component.html',
  styleUrl: './add-customization-modal.component.css',
})
export class AddCustomizationModalComponent {
  readonly dialogRef = inject(MatDialogRef<AddCustomizationModalComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  customizationForm: FormGroup;
  restaurantService = inject(RestaurantService);

  allCustomization: any = [];
  isDuplicateName = false;
  restaurantPosProvider: string = 'fnf';
  openMenuIndex: number | null = null;
  saveAttempted = false;

  constructor(private fb: FormBuilder) {
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
    });
    this.customizationForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(120)]],
      remarks: [''],
      options: this.fb.array([], [Validators.required]),
      isMultiSelect: [false],
      requireSelection: [false],
      minSelections: [{ value: 0, disabled: true }, [Validators.min(0)]],
      maxSelections: [{ value: null, disabled: true }, [Validators.min(0)]],
      priceDiffersForEachOption: [false],
      // Hidden from customer apps (mobile, order, togo) when false; dashboard,
      // POS and waiter always see the group.
      isCustomerVisible: [true],
    });
    if (!this.data.customization) {
      this.addOption();
    }
  }

  ngOnInit(): void {
    this.allCustomization = this.data?.allCustomization || [];

    this.customizationForm
      .get('priceDiffersForEachOption')
      ?.valueChanges.subscribe((value: boolean) => {
        this.updatePriceControlsDisabledState(value);
      });

    this.customizationForm.get('isMultiSelect')?.valueChanges.subscribe((multi: boolean) => {
      this.syncQuantityControls(multi);
      if (!multi) {
        this.enforceSingleDefault();
      }
    });

    this.customizationForm.get('requireSelection')?.valueChanges.subscribe(() => {
      this.syncQuantityControls(this.isMultiSelect);
    });

    const customization = this.data.customization;
    if (customization) {
      this.customizationForm.patchValue({
        name: customization.name,
        description: customization.description || '',
        remarks: customization.remarks,
        isMultiSelect: !!customization.is_multi_select,
        requireSelection: (customization.min_selections ?? 0) >= 1,
        minSelections: customization.min_selections ?? 0,
        maxSelections: customization.max_selections ?? null,
        priceDiffersForEachOption: customization.is_price_different || false,
        // Missing/undefined means visible (rows stored before the field existed).
        isCustomerVisible: customization.is_customer_visible !== false,
      });

      // Price-differs is fixed after creation.
      this.customizationForm.get('priceDiffersForEachOption')?.disable();

      (customization.types || []).forEach((e: any) => {
        this.addOption(
          e.value,
          e.price,
          e.is_default || false,
          e.type_id,
          e.description,
          e.in_stock !== false,
        );
      });

      if (this.isPosManagedEdit) {
        this.customizationForm.get('name')?.disable();
        this.customizationForm.get('description')?.disable();
        this.customizationForm.get('remarks')?.disable();
        this.customizationForm.get('isMultiSelect')?.disable();
        this.customizationForm.get('requireSelection')?.disable();
        this.customizationForm.get('minSelections')?.disable();
        this.customizationForm.get('maxSelections')?.disable();
        this.getOptions.disable();
      }
    }

    this.syncQuantityControls(this.isMultiSelect);
    this.updatePriceControlsDisabledState(
      this.customizationForm.get('priceDiffersForEachOption')?.value || false,
    );
  }

  get isMultiSelect(): boolean {
    return !!this.customizationForm.get('isMultiSelect')?.value;
  }

  get isEditMode(): boolean {
    return !!this.data?.customization;
  }

  get isPosManaged() {
    return isPosManaged(this.restaurantPosProvider);
  }

  // POS-managed customizations sync from Clover/Square: content is read-only
  // here, but customer visibility is FnF-only state and stays editable.
  get isPosManagedEdit() {
    return this.isPosManaged && !!this.data?.customization;
  }

  get getOptions() {
    return this.customizationForm.get('options') as FormArray;
  }

  get defaultCount(): number {
    return this.getOptions.getRawValue().filter((o: any) => o.isChecked).length;
  }

  get optionCount(): number {
    return this.getOptions.length;
  }

  /// The smallest minimum this group can carry.
  ///
  /// A pre-selected option does not raise this floor: the customer apps open
  /// with a default selected and let it be cleared, so an optional group may
  /// carry one.
  get minAllowed(): number {
    return this.customizationForm.get('requireSelection')?.value ? 1 : 0;
  }

  /// The largest maximum this group can carry: one for a single-select group,
  /// and otherwise the number of options — a cap above that can never bind,
  /// and the service rejects it.
  get maxAllowed(): number {
    return this.isMultiSelect ? Math.max(this.optionCount, 1) : 1;
  }

  // Mirrors the backend write rules so the user sees the problem before a 400.
  get ruleError(): string | null {
    const raw = this.customizationForm.getRawValue();
    const optionCount = (raw.options || []).length;
    const min = raw.minSelections ?? 0;
    const max = raw.maxSelections;
    if (max !== null && max !== '' && max !== undefined) {
      if (min > max) {
        return 'Minimum selections cannot be greater than maximum selections.';
      }
      if (this.defaultCount > max) {
        return `Pre-selected options (${this.defaultCount}) cannot exceed maximum selections (${max}).`;
      }
    }
    if (min > optionCount) {
      return `Minimum selections (${min}) cannot exceed the number of options (${optionCount}).`;
    }
    if (max !== null && max !== '' && max !== undefined && max > optionCount) {
      return `Maximum selections (${max}) cannot exceed the number of options (${optionCount}).`;
    }
    return null;
  }

  syncQuantityControls(multi: boolean) {
    if (this.isPosManagedEdit) {
      return;
    }
    const minControl = this.customizationForm.get('minSelections');
    const maxControl = this.customizationForm.get('maxSelections');
    const minAllowed = this.minAllowed;

    if (!multi) {
      // Single-select is exactly one choice: the minimum is 0 (optional) or 1
      // (required, or forced by a pre-selected option), and the maximum is 1.
      // Neither is the user's to type.
      minControl?.setValue(minAllowed, { emitEvent: false });
      maxControl?.setValue(1, { emitEvent: false });
      minControl?.disable({ emitEvent: false });
      maxControl?.disable({ emitEvent: false });
      return;
    }

    minControl?.enable({ emitEvent: false });
    maxControl?.enable({ emitEvent: false });

    const maxAllowed = this.maxAllowed;
    // Clamp rather than blank: the user gets a value they can see and adjust,
    // instead of an empty field they have to guess at.
    const min = this.clamp(this.toCount(minControl?.value) ?? minAllowed, minAllowed, maxAllowed);
    if (minControl?.value !== min) {
      minControl?.setValue(min, { emitEvent: false });
    }

    // Empty stays empty: no maximum is a valid answer, and the read path
    // derives one from the option count.
    const rawMax = this.toCount(maxControl?.value);
    if (rawMax !== null) {
      const max = this.clamp(rawMax, Math.max(min, this.defaultCount, 1), maxAllowed);
      if (maxControl?.value !== max) {
        maxControl?.setValue(max, { emitEvent: false });
      }
    }
  }

  private toCount(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.floor(parsed) : null;
  }

  private clamp(value: number, low: number, high: number): number {
    return Math.min(Math.max(value, low), high);
  }

  toggleControl(controlName: string) {
    const control = this.customizationForm.get(controlName);
    if (control && !control.disabled) {
      control.setValue(!control.value);
    }
  }

  enforceSingleDefault() {
    const checked = this.getOptions.controls.filter((c) => c.get('isChecked')?.value);
    checked.slice(1).forEach((c) => c.get('isChecked')?.setValue(false, { emitEvent: false }));
  }

  onDefaultToggle(index: number) {
    const control = this.getOptions.at(index);
    if (!control || control.disabled) {
      return;
    }
    const next = !control.get('isChecked')?.value;
    if (next && !this.isMultiSelect) {
      // Single-select allows at most one default.
      this.getOptions.controls.forEach((c, i) => {
        c.get('isChecked')?.setValue(i === index, { emitEvent: false });
      });
    } else {
      control.get('isChecked')?.setValue(next);
    }
    this.getOptions.controls.forEach((c) => this.validateCheckedOptionPrice(c));
    // A default does not move the minimum, but it does floor the maximum: the
    // service refuses a maximum below the number of pre-selected options.
    this.syncQuantityControls(this.isMultiSelect);
  }

  toggleAvailability(index: number) {
    const control = this.getOptions.at(index);
    if (!control || control.disabled) {
      return;
    }
    control.get('inStock')?.setValue(!control.get('inStock')?.value);
    this.openMenuIndex = null;
  }

  toggleRowMenu(index: number, event: Event) {
    event.stopPropagation();
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  addOption(
    name?: any,
    price?: any,
    isChecked?: boolean,
    typeId?: any,
    description?: any,
    inStock: boolean = true,
  ) {
    const priceDiffersForEachOption =
      this.customizationForm.get('priceDiffersForEachOption')?.value || false;

    const group: any = {
      isChecked: [isChecked || false],
      name: [name || null, [Validators.required, this.duplicateOptionValidator.bind(this)]],
      description: [description || '', [Validators.maxLength(300)]],
      price: [
        // Kept as a "5.00"-style string so the input always shows two
        // decimals; parsed back to a number on save.
        { value: this.toPriceString(price), disabled: !priceDiffersForEachOption },
        [Validators.required, Validators.min(0), Validators.max(10000)],
      ],
      inStock: [inStock],
    };
    if (typeId) {
      group.type_id = [typeId];
    }
    const optionControl = this.fb.group(group);

    optionControl.get('name')?.valueChanges.subscribe(() => this.checkDuplicateOptions());
    optionControl
      .get('isChecked')
      ?.valueChanges.subscribe(() => this.validateCheckedOptionPrice(optionControl));
    optionControl
      .get('price')
      ?.valueChanges.subscribe(() => this.validateCheckedOptionPrice(optionControl));

    this.getOptions.push(optionControl);
    // More options raises the ceiling over the maximum.
    this.syncQuantityControls(this.isMultiSelect);
  }

  toPriceString(value: any): string {
    const parsed = parseFloat(value);
    return (isNaN(parsed) ? 0 : parsed).toFixed(2);
  }

  formatPrice(index: number) {
    const control = this.getOptions.at(index)?.get('price');
    if (control && !control.disabled) {
      control.setValue(this.toPriceString(control.value));
    }
  }

  removeOption(index: number) {
    this.getOptions.removeAt(index);
    this.openMenuIndex = null;
    this.checkDuplicateOptions();
    // Fewer options lowers the ceiling over the maximum.
    this.syncQuantityControls(this.isMultiSelect);
  }

  // A pre-selected (default) option is included in the base price: its price
  // is locked at $0.00. Mirrors the backend rule.
  validateCheckedOptionPrice(optionControl: any) {
    const isChecked = optionControl.get('isChecked')?.value;
    const price = parseFloat(optionControl.get('price')?.value);

    if (isChecked && !isNaN(price) && price !== 0) {
      const currentErrors = optionControl.get('price')?.errors || {};
      optionControl.get('price')?.setErrors({ ...currentErrors, defaultOptionPrice: true });
    } else {
      const priceControl = optionControl.get('price');
      if (priceControl?.errors?.['defaultOptionPrice']) {
        const errors = { ...priceControl.errors };
        delete errors['defaultOptionPrice'];
        priceControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
    }
  }

  updatePriceControlsDisabledState(priceDiffersForEachOption: boolean) {
    const isEditMode = this.isEditMode;
    this.getOptions.controls.forEach((control) => {
      const priceControl = control.get('price');
      if (priceControl) {
        if (priceDiffersForEachOption && !this.isPosManagedEdit) {
          priceControl.enable();
        } else {
          // In edit mode, keep existing prices visible (read-only); only zero when adding new
          if (!isEditMode) {
            priceControl.setValue('0.00');
          }
          priceControl.disable();
          if (priceControl.errors?.['defaultOptionPrice']) {
            const errors = { ...priceControl.errors };
            delete errors['defaultOptionPrice'];
            priceControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
          }
        }
      }
    });
  }

  duplicateOptionValidator(control: any) {
    if (!control || !control.value) {
      return null;
    }
    const currentValue = control.value.toLowerCase().trim();
    if (!currentValue) {
      return null;
    }
    const options = this.getOptions.value;
    const duplicateCount = options.filter(
      (opt: any) => opt.name && opt.name.toLowerCase().trim() === currentValue,
    ).length;
    return duplicateCount > 1 ? { duplicate: true } : null;
  }

  checkDuplicateOptions() {
    const options = this.getOptions.value;
    const names = options
      .map((opt: any) => opt.name?.toLowerCase().trim())
      .filter((name: any) => name);
    const uniqueNames = new Set(names);
    const hasDuplicates = names.length !== uniqueNames.size;

    this.getOptions.controls.forEach((control) => {
      const nameControl = control.get('name');
      if (nameControl && nameControl.value) {
        const currentName = nameControl.value.toLowerCase().trim();
        const duplicateCount = names.filter((name: string) => name === currentName).length;
        if (duplicateCount > 1) {
          nameControl.setErrors({ ...(nameControl.errors || {}), duplicate: true });
        } else if (nameControl.errors?.['duplicate']) {
          const errors = { ...nameControl.errors };
          delete errors['duplicate'];
          nameControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
      }
    });
    return hasDuplicates;
  }

  checkDuplicateName() {
    const value = (this.customizationForm.get('name')?.value || '').toLowerCase();
    this.isDuplicateName = !!this.allCustomization.find(
      (f: any) => f.name?.toLowerCase() === value && f.name !== this.data.customization?.name,
    );
  }

  preventNegativeInput(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  save() {
    this.saveAttempted = true;
    this.customizationForm.markAllAsTouched();
    if (this.customizationForm.invalid || this.isDuplicateName || this.ruleError) {
      return;
    }

    const formValue = this.customizationForm.getRawValue();
    const isMulti = !!formValue.isMultiSelect;
    const options = this.getOptions.getRawValue().map((o: any) => ({
      ...o,
      price: parseFloat(o.price) || 0,
    }));

    const result: any = {
      name: formValue.name,
      description: formValue.description,
      remarks: formValue.remarks,
      inputType: isMulti ? 'Multi Selection' : 'Single Selection',
      options,
      // Legacy `default` field consumed by older clients.
      defaultValue: isMulti
        ? options.filter((e: any) => e.isChecked).map((e: any) => e.name)
        : (options.find((e: any) => e.isChecked)?.name ?? null),
      priceDiffersForEachOption: formValue.priceDiffersForEachOption,
      isCustomerVisible: formValue.isCustomerVisible,
      minSelections: formValue.minSelections ?? 0,
      maxSelections: isMulti ? (formValue.maxSelections ?? null) : 1,
    };
    this.dialogRef.close(result);
  }

  close() {
    this.dialogRef.close();
  }
}
